import { Chacha20Poly1305 } from "@hpke/chacha20poly1305";
import {
  Dhkem,
  HkdfSha256Native,
  hmac,
  KemId,
  sha256,
  toArrayBuffer,
  toUint8Array,
} from "@hpke/common";
import { CipherSuite, type RecipientContext, type SenderContext } from "@hpke/core";
import { X25519 } from "@hpke/dhkem-x25519";

export const RELAY_PAYLOAD_PROTOCOL_VERSION = 4 as const;
export const RELAY_PAYLOAD_SUITE =
  "DHKEM_X25519_HKDF_SHA256/HKDF_SHA256/CHACHA20_POLY1305/AUTH" as const;
export const RELAY_PAYLOAD_MAX_RECORDS = 2 ** 31;
export const RELAY_PAYLOAD_MAX_CHANNEL_BYTES = 1024 * 1024 * 1024;
export const RELAY_PAYLOAD_MAX_CHANNEL_AGE_MS = 30 * 60 * 1000;
export const RELAY_PAYLOAD_MAX_PLAINTEXT_BYTES = 16 * 1024 * 1024;
export const RELAY_PAYLOAD_MAX_TICKET_BYTES = 64 * 1024;

const HPKE_TAG_BYTES = 16;
const ENDPOINT_KEY_BYTES = 32;
const X25519_FIELD_PRIME = (1n << 255n) - 19n;
const textEncoder = new TextEncoder();
const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function littleEndianHexToBigInt(value: string): bigint {
  let output = 0n;
  for (let index = 0; index < value.length; index += 2) {
    output |= BigInt(Number.parseInt(value.slice(index, index + 2), 16)) << BigInt((index / 2) * 8);
  }
  return output;
}

const X25519_LOW_ORDER_COORDINATES = new Set([
  0n,
  1n,
  littleEndianHexToBigInt("e0eb7a7c3b41b8ae1656e3faf19fc46ada098deb9c32b1fd866205165f49b800"),
  littleEndianHexToBigInt("5f9c95bca3508c24b1d0b1559c83ef5b04445cc4581c8e86d8224eddd09f1157"),
  X25519_FIELD_PRIME - 1n,
]);

const EMPTY = new Uint8Array();

class PortableHkdfSha256 extends HkdfSha256Native {
  override async extract(
    salt: ArrayBufferLike | ArrayBufferView,
    ikm: ArrayBufferLike | ArrayBufferView,
  ): Promise<ArrayBuffer> {
    const normalizedSalt =
      salt.byteLength === 0 ? new Uint8Array(this.hashSize) : toUint8Array(salt);
    return toArrayBuffer(hmac(sha256, normalizedSalt, toUint8Array(ikm)));
  }

  override async expand(
    prk: ArrayBufferLike | ArrayBufferView,
    info: ArrayBufferLike | ArrayBufferView,
    length: number,
  ): Promise<ArrayBuffer> {
    if (!Number.isSafeInteger(length) || length < 0 || length > 255 * this.hashSize) {
      throw new Error("HKDF output length exceeds the RFC 5869 limit.");
    }
    const key = toUint8Array(prk);
    const context = toUint8Array(info);
    const output = new Uint8Array(length);
    let previous = EMPTY;
    for (let counter = 1, offset = 0; offset < output.byteLength; counter += 1) {
      const blockInput = concat([previous, context, Uint8Array.of(counter)]);
      previous = new Uint8Array(hmac(sha256, key, blockInput));
      const remaining = output.byteLength - offset;
      output.set(previous.subarray(0, Math.min(previous.byteLength, remaining)), offset);
      offset += Math.min(previous.byteLength, remaining);
    }
    return toArrayBuffer(output);
  }

  override async extractAndExpand(
    salt: ArrayBufferLike | ArrayBufferView,
    ikm: ArrayBufferLike | ArrayBufferView,
    info: ArrayBufferLike | ArrayBufferView,
    length: number,
  ): Promise<ArrayBuffer> {
    return this.expand(await this.extract(salt, ikm), info, length);
  }
}

class PortableDhkemX25519HkdfSha256 extends Dhkem {
  override readonly secretSize = 32;
  override readonly encSize = 32;
  override readonly publicKeySize = 32;
  override readonly privateKeySize = 32;

  constructor(kdf: PortableHkdfSha256) {
    super(KemId.DhkemX25519HkdfSha256, new X25519(kdf), kdf);
  }
}

const suiteKdf = new PortableHkdfSha256();
const suite = new CipherSuite({
  kem: new PortableDhkemX25519HkdfSha256(suiteKdf),
  kdf: new PortableHkdfSha256(),
  aead: new Chacha20Poly1305(),
});

export type RelayPayloadDirection = "control-to-runtime" | "runtime-to-control";
export type RelayPayloadFrameClass = "handshake" | "application" | "close";

export type RelayPayloadChannelErrorCode =
  | "channel_authentication_failed"
  | "channel_limit_reached"
  | "channel_sequence_invalid"
  | "channel_ticket_binding_invalid"
  | "endpoint_key_invalid";

export class RelayPayloadChannelFailure extends Error {
  override readonly name = "RelayPayloadChannelFailure";

  constructor(
    readonly code: RelayPayloadChannelErrorCode,
    message: string,
  ) {
    super(message);
  }
}

export interface RelayPayloadBinding {
  readonly channelId: string;
  readonly direction: RelayPayloadDirection;
  readonly runtimeId: string;
  readonly connectionEpoch: number;
  readonly ticketDigest: Uint8Array;
}

export interface RelayEncryptedFrame {
  readonly type: "relay.encrypted-frame";
  readonly protocolVersion: 4;
  readonly channelId: string;
  readonly direction: RelayPayloadDirection;
  readonly frameClass: RelayPayloadFrameClass;
  readonly sequence: number;
  readonly ciphertext: string;
}

export interface RelayPayloadDigestProvider {
  readonly sha256: (value: Uint8Array) => Promise<Uint8Array>;
}

export interface RelayPayloadRandomSource {
  readonly randomBytes: (length: number) => Promise<Uint8Array> | Uint8Array;
}

export interface RelayPayloadSender {
  readonly encapsulatedKey: string;
  readonly seal: (input: {
    readonly frameClass: RelayPayloadFrameClass;
    readonly plaintext: Uint8Array;
  }) => Promise<RelayEncryptedFrame>;
}

export interface RelayPayloadRecipient {
  readonly open: (frame: RelayEncryptedFrame) => Promise<Uint8Array>;
}

function bytes(value: ArrayBuffer): Uint8Array {
  return new Uint8Array(value.slice(0));
}

function arrayBuffer(value: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}

function concat(values: ReadonlyArray<Uint8Array>): Uint8Array {
  const length = values.reduce((total, value) => total + value.byteLength, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const value of values) {
    output.set(value, offset);
    offset += value.byteLength;
  }
  return output;
}

function uint32(value: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffffff) {
    throw new RelayPayloadChannelFailure("channel_ticket_binding_invalid", "Invalid uint32 value.");
  }
  const output = new Uint8Array(4);
  new DataView(output.buffer).setUint32(0, value, false);
  return output;
}

function uint64(value: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RelayPayloadChannelFailure("channel_ticket_binding_invalid", "Invalid uint64 value.");
  }
  const output = new Uint8Array(8);
  new DataView(output.buffer).setBigUint64(0, BigInt(value), false);
  return output;
}

function lengthPrefixed(value: Uint8Array): Uint8Array {
  return concat([uint32(value.byteLength), value]);
}

function directionByte(direction: RelayPayloadDirection): Uint8Array {
  return Uint8Array.of(direction === "control-to-runtime" ? 0 : 1);
}

function frameClassByte(frameClass: RelayPayloadFrameClass): Uint8Array {
  if (frameClass === "handshake") return Uint8Array.of(0);
  if (frameClass === "application") return Uint8Array.of(1);
  return Uint8Array.of(2);
}

function encodeBase64Url(value: Uint8Array): string {
  let output = "";
  for (let index = 0; index < value.byteLength; index += 3) {
    const first = value[index] ?? 0;
    const second = value[index + 1] ?? 0;
    const third = value[index + 2] ?? 0;
    const combined = (first << 16) | (second << 8) | third;
    output += BASE64URL_ALPHABET[(combined >>> 18) & 63];
    output += BASE64URL_ALPHABET[(combined >>> 12) & 63];
    if (index + 1 < value.byteLength) output += BASE64URL_ALPHABET[(combined >>> 6) & 63];
    if (index + 2 < value.byteLength) output += BASE64URL_ALPHABET[combined & 63];
  }
  return output;
}

function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/.test(value) || value.length % 4 === 1) {
    throw new RelayPayloadChannelFailure(
      "channel_authentication_failed",
      "Encrypted relay frame is not canonical base64url.",
    );
  }
  const output = new Uint8Array(Math.floor((value.length * 6) / 8));
  let bitBuffer = 0;
  let bitCount = 0;
  let outputIndex = 0;
  for (const character of value) {
    const decoded = BASE64URL_ALPHABET.indexOf(character);
    bitBuffer = (bitBuffer << 6) | decoded;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      output[outputIndex] = (bitBuffer >>> bitCount) & 0xff;
      outputIndex += 1;
    }
  }
  if (encodeBase64Url(output) !== value) {
    throw new RelayPayloadChannelFailure(
      "channel_authentication_failed",
      "Encrypted relay frame has a non-canonical base64url encoding.",
    );
  }
  return output;
}

function isLowOrderX25519PublicKey(value: Uint8Array): boolean {
  const normalized = new Uint8Array(value);
  normalized[ENDPOINT_KEY_BYTES - 1] = (normalized[ENDPOINT_KEY_BYTES - 1] ?? 0) & 0x7f;
  let coordinate = 0n;
  for (let index = 0; index < normalized.byteLength; index += 1) {
    coordinate |= BigInt(normalized[index] ?? 0) << BigInt(index * 8);
  }
  return X25519_LOW_ORDER_COORDINATES.has(coordinate % X25519_FIELD_PRIME);
}

const defaultDigestProvider: RelayPayloadDigestProvider = {
  async sha256(value) {
    if (globalThis.crypto?.subtle === undefined) {
      throw new RelayPayloadChannelFailure(
        "channel_authentication_failed",
        "SHA-256 is unavailable in this runtime.",
      );
    }
    return bytes(await globalThis.crypto.subtle.digest("SHA-256", arrayBuffer(value)));
  },
};

const defaultRandomSource: RelayPayloadRandomSource = {
  randomBytes(length) {
    if (globalThis.crypto?.getRandomValues === undefined) {
      throw new RelayPayloadChannelFailure(
        "endpoint_key_invalid",
        "A cryptographically secure random source is unavailable.",
      );
    }
    return globalThis.crypto.getRandomValues(new Uint8Array(length));
  },
};

function assertIdentifier(value: string, label: string): void {
  const length = textEncoder.encode(value).byteLength;
  if (length === 0 || length > 256) {
    throw new RelayPayloadChannelFailure(
      "channel_ticket_binding_invalid",
      `${label} must contain between 1 and 256 UTF-8 bytes.`,
    );
  }
}

function assertControlTicket(value: string): void {
  const length = textEncoder.encode(value).byteLength;
  if (length === 0 || length > RELAY_PAYLOAD_MAX_TICKET_BYTES) {
    throw new RelayPayloadChannelFailure(
      "channel_ticket_binding_invalid",
      `Control ticket must contain between 1 and ${RELAY_PAYLOAD_MAX_TICKET_BYTES} UTF-8 bytes.`,
    );
  }
}

export async function createRelayPayloadBinding(
  input: {
    readonly exactCompactTicket: string;
    readonly channelId: string;
    readonly direction: RelayPayloadDirection;
    readonly runtimeId: string;
    readonly connectionEpoch: number;
  },
  digestProvider: RelayPayloadDigestProvider = defaultDigestProvider,
): Promise<RelayPayloadBinding> {
  assertControlTicket(input.exactCompactTicket);
  assertIdentifier(input.channelId, "Channel ID");
  assertIdentifier(input.runtimeId, "Runtime ID");
  uint64(input.connectionEpoch);
  const ticketDigest = await digestProvider.sha256(textEncoder.encode(input.exactCompactTicket));
  if (ticketDigest.byteLength !== 32) {
    throw new RelayPayloadChannelFailure(
      "channel_ticket_binding_invalid",
      "The control ticket digest must be 32 bytes.",
    );
  }
  return { ...input, ticketDigest: new Uint8Array(ticketDigest) };
}

export function relayPayloadTicketDigestBase64Url(binding: RelayPayloadBinding): string {
  return encodeBase64Url(binding.ticketDigest);
}

export async function generateRelayChannelNonce(
  randomSource: RelayPayloadRandomSource = defaultRandomSource,
): Promise<string> {
  const nonce = new Uint8Array(await randomSource.randomBytes(ENDPOINT_KEY_BYTES));
  if (nonce.byteLength !== ENDPOINT_KEY_BYTES) {
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      `The endpoint random source must return ${ENDPOINT_KEY_BYTES} bytes.`,
    );
  }
  try {
    return encodeBase64Url(nonce);
  } finally {
    nonce.fill(0);
  }
}

export function relayPayloadInfo(binding: RelayPayloadBinding): Uint8Array {
  return concat([
    textEncoder.encode("forge-relay-v4"),
    Uint8Array.of(0),
    binding.ticketDigest,
    directionByte(binding.direction),
  ]);
}

export function relayPayloadAad(input: {
  readonly binding: RelayPayloadBinding;
  readonly frameClass: RelayPayloadFrameClass;
  readonly sequence: number;
  readonly ciphertextLength: number;
}): Uint8Array {
  return concat(
    [
      textEncoder.encode("forge-relay"),
      uint32(RELAY_PAYLOAD_PROTOCOL_VERSION),
      input.binding.ticketDigest,
      textEncoder.encode(input.binding.channelId),
      directionByte(input.binding.direction),
      frameClassByte(input.frameClass),
      uint64(input.sequence),
      textEncoder.encode(input.binding.runtimeId),
      uint64(input.binding.connectionEpoch),
      uint32(input.ciphertextLength),
    ].map(lengthPrefixed),
  );
}

export async function generateRelayEndpointKeyPair(
  randomSource: RelayPayloadRandomSource = defaultRandomSource,
): Promise<CryptoKeyPair> {
  const seed = new Uint8Array(await randomSource.randomBytes(ENDPOINT_KEY_BYTES));
  if (seed.byteLength !== ENDPOINT_KEY_BYTES) {
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      `The endpoint random source must return ${ENDPOINT_KEY_BYTES} bytes.`,
    );
  }
  try {
    return await suite.kem.deriveKeyPair(seed);
  } catch (error) {
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      error instanceof Error ? error.message : "Endpoint key generation failed.",
    );
  } finally {
    seed.fill(0);
  }
}

export async function serializeRelayEndpointPublicKey(publicKey: CryptoKey): Promise<string> {
  try {
    return encodeBase64Url(bytes(await suite.kem.serializePublicKey(publicKey)));
  } catch (error) {
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      error instanceof Error ? error.message : "Endpoint public-key serialization failed.",
    );
  }
}

export function relayEndpointKeyId(
  role: "control" | "runtime",
  serializedPublicKey: string,
): string {
  if (!/^[A-Za-z0-9_-]{43}$/u.test(serializedPublicKey)) {
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      "The endpoint key ID requires a canonical X25519 public key.",
    );
  }
  return `${role}:${serializedPublicKey}`;
}

export async function deserializeRelayEndpointPublicKey(value: string): Promise<CryptoKey> {
  try {
    const serialized = decodeBase64Url(value);
    if (serialized.byteLength !== ENDPOINT_KEY_BYTES)
      throw new Error("Invalid endpoint key length.");
    if (isLowOrderX25519PublicKey(serialized)) {
      throw new RelayPayloadChannelFailure(
        "endpoint_key_invalid",
        "Low-order X25519 endpoint public keys are not allowed.",
      );
    }
    return await suite.kem.deserializePublicKey(serialized);
  } catch (error) {
    if (error instanceof RelayPayloadChannelFailure) throw error;
    throw new RelayPayloadChannelFailure(
      "endpoint_key_invalid",
      error instanceof Error ? error.message : "Endpoint public-key deserialization failed.",
    );
  }
}

class OperationQueue {
  private tail = Promise.resolve();

  run<Result>(operation: () => Promise<Result>): Promise<Result> {
    const result = this.tail.then(operation, operation);
    this.tail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }
}

class ChannelBudget {
  private readonly startedAt = Date.now();
  private records = 0;
  private bytes = 0;

  assertAvailable(nextBytes: number): void {
    if (
      this.records >= RELAY_PAYLOAD_MAX_RECORDS ||
      this.bytes + nextBytes > RELAY_PAYLOAD_MAX_CHANNEL_BYTES ||
      Date.now() - this.startedAt >= RELAY_PAYLOAD_MAX_CHANNEL_AGE_MS
    ) {
      throw new RelayPayloadChannelFailure(
        "channel_limit_reached",
        "The encrypted relay channel must be renewed before another record is sent.",
      );
    }
  }

  commit(recordBytes: number): void {
    this.records += 1;
    this.bytes += recordBytes;
  }
}

function assertFrameBinding(frame: RelayEncryptedFrame, binding: RelayPayloadBinding): void {
  if (
    frame.type !== "relay.encrypted-frame" ||
    frame.protocolVersion !== RELAY_PAYLOAD_PROTOCOL_VERSION ||
    frame.channelId !== binding.channelId ||
    frame.direction !== binding.direction ||
    !["handshake", "application", "close"].includes(frame.frameClass)
  ) {
    throw new RelayPayloadChannelFailure(
      "channel_ticket_binding_invalid",
      "Encrypted relay frame does not match the CMMD-ticket-bound channel.",
    );
  }
}

class HpkeRelayPayloadSender implements RelayPayloadSender {
  readonly encapsulatedKey: string;
  private readonly queue = new OperationQueue();
  private readonly budget = new ChannelBudget();
  private sequence = 0;

  constructor(
    private readonly binding: RelayPayloadBinding,
    private readonly context: SenderContext,
  ) {
    this.encapsulatedKey = encodeBase64Url(bytes(context.enc));
  }

  seal(input: {
    readonly frameClass: RelayPayloadFrameClass;
    readonly plaintext: Uint8Array;
  }): Promise<RelayEncryptedFrame> {
    return this.queue.run(async () => {
      if (input.plaintext.byteLength > RELAY_PAYLOAD_MAX_PLAINTEXT_BYTES) {
        throw new RelayPayloadChannelFailure(
          "channel_limit_reached",
          "Encrypted relay plaintext exceeds the per-record byte limit.",
        );
      }
      const ciphertextLength = input.plaintext.byteLength + HPKE_TAG_BYTES;
      this.budget.assertAvailable(ciphertextLength);
      const sequence = this.sequence;
      const aad = relayPayloadAad({
        binding: this.binding,
        frameClass: input.frameClass,
        sequence,
        ciphertextLength,
      });
      let ciphertext: Uint8Array;
      try {
        ciphertext = bytes(await this.context.seal(input.plaintext, aad));
      } catch (error) {
        throw new RelayPayloadChannelFailure(
          "channel_authentication_failed",
          error instanceof Error ? error.message : "Relay payload encryption failed.",
        );
      }
      if (ciphertext.byteLength !== ciphertextLength) {
        throw new RelayPayloadChannelFailure(
          "channel_authentication_failed",
          "Relay payload encryption returned an invalid ciphertext length.",
        );
      }
      this.sequence += 1;
      this.budget.commit(ciphertext.byteLength);
      return {
        type: "relay.encrypted-frame",
        protocolVersion: RELAY_PAYLOAD_PROTOCOL_VERSION,
        channelId: this.binding.channelId,
        direction: this.binding.direction,
        frameClass: input.frameClass,
        sequence,
        ciphertext: encodeBase64Url(ciphertext),
      };
    });
  }
}

class HpkeRelayPayloadRecipient implements RelayPayloadRecipient {
  private readonly queue = new OperationQueue();
  private readonly budget = new ChannelBudget();
  private sequence = 0;

  constructor(
    private readonly binding: RelayPayloadBinding,
    private readonly context: RecipientContext,
  ) {}

  open(frame: RelayEncryptedFrame): Promise<Uint8Array> {
    return this.queue.run(async () => {
      assertFrameBinding(frame, this.binding);
      if (!Number.isSafeInteger(frame.sequence) || frame.sequence !== this.sequence) {
        throw new RelayPayloadChannelFailure(
          "channel_sequence_invalid",
          `Expected encrypted relay sequence ${this.sequence}, received ${String(frame.sequence)}.`,
        );
      }
      const ciphertext = decodeBase64Url(frame.ciphertext);
      this.budget.assertAvailable(ciphertext.byteLength);
      const aad = relayPayloadAad({
        binding: this.binding,
        frameClass: frame.frameClass,
        sequence: frame.sequence,
        ciphertextLength: ciphertext.byteLength,
      });
      let plaintext: Uint8Array;
      try {
        plaintext = bytes(await this.context.open(ciphertext, aad));
      } catch (error) {
        throw new RelayPayloadChannelFailure(
          "channel_authentication_failed",
          error instanceof Error ? error.message : "Relay payload authentication failed.",
        );
      }
      if (plaintext.byteLength > RELAY_PAYLOAD_MAX_PLAINTEXT_BYTES) {
        throw new RelayPayloadChannelFailure(
          "channel_limit_reached",
          "Decrypted relay plaintext exceeds the per-record byte limit.",
        );
      }
      this.sequence += 1;
      this.budget.commit(ciphertext.byteLength);
      return plaintext;
    });
  }
}

export async function createRelayPayloadSender(input: {
  readonly binding: RelayPayloadBinding;
  readonly senderKey: CryptoKeyPair;
  readonly recipientPublicKey: CryptoKey;
  readonly randomSource?: RelayPayloadRandomSource;
}): Promise<RelayPayloadSender> {
  let ephemeralKeyMaterial: Uint8Array | null = null;
  try {
    ephemeralKeyMaterial = new Uint8Array(
      await (input.randomSource ?? defaultRandomSource).randomBytes(ENDPOINT_KEY_BYTES),
    );
    if (ephemeralKeyMaterial.byteLength !== ENDPOINT_KEY_BYTES) {
      throw new Error(`The endpoint random source must return ${ENDPOINT_KEY_BYTES} bytes.`);
    }
    const context = await suite.createSenderContext({
      recipientPublicKey: input.recipientPublicKey,
      senderKey: input.senderKey,
      info: relayPayloadInfo(input.binding),
      ekm: ephemeralKeyMaterial,
    });
    return new HpkeRelayPayloadSender(input.binding, context);
  } catch (error) {
    throw new RelayPayloadChannelFailure(
      "channel_authentication_failed",
      error instanceof Error ? error.message : "Relay payload sender setup failed.",
    );
  } finally {
    ephemeralKeyMaterial?.fill(0);
  }
}

export async function createRelayPayloadRecipient(input: {
  readonly binding: RelayPayloadBinding;
  readonly recipientKey: CryptoKeyPair;
  readonly senderPublicKey: CryptoKey;
  readonly encapsulatedKey: string;
}): Promise<RelayPayloadRecipient> {
  try {
    const context = await suite.createRecipientContext({
      recipientKey: input.recipientKey,
      senderPublicKey: input.senderPublicKey,
      enc: decodeBase64Url(input.encapsulatedKey),
      info: relayPayloadInfo(input.binding),
    });
    return new HpkeRelayPayloadRecipient(input.binding, context);
  } catch (error) {
    if (error instanceof RelayPayloadChannelFailure) throw error;
    throw new RelayPayloadChannelFailure(
      "channel_authentication_failed",
      error instanceof Error ? error.message : "Relay payload recipient setup failed.",
    );
  }
}
